
import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { differenceInDays, parseISO } from 'date-fns';
// Order type from '@/components/profile/order-types' is not directly used, specific type below
// import { Order } from '@/components/profile/order-types';

export const useOrderAutoComplete = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isProcessingRef = useRef(false); // To prevent concurrent executions

  const checkAndCompleteOrders = useCallback(async () => {
    if (isProcessingRef.current) {
      // console.log("Order auto-completion already in progress. Skipping.");
      return;
    }
    isProcessingRef.current = true;

    // console.log("Checking and completing orders...");
    try {
      const { data: deliveredOrders, error } = await supabase
        .from('orders')
        .select('id, updated_at') // OPTIMIZATION: Select only necessary fields
        .eq('status', 'delivered');

      if (error) {
        console.error("Error fetching delivered orders for auto-completion:", error); // IMPROVEMENT: Log error
        isProcessingRef.current = false;
        return; 
      }

      if (!deliveredOrders || deliveredOrders.length === 0) {
        // console.log("No delivered orders to process for auto-completion.");
        isProcessingRef.current = false;
        return;
      }

      const now = new Date();
      const ordersToComplete: string[] = [];

      (deliveredOrders as Array<{ id: string; updated_at: string }>).forEach((order) => {
        try {
          const updatedAt = parseISO(order.updated_at); 
          const daysSinceUpdate = differenceInDays(now, updatedAt);

          if (daysSinceUpdate >= 30 && order.id) {
            ordersToComplete.push(order.id);
          }
        } catch (parseError) {
          console.error(`Error parsing date for order ${order.id}:`, parseError);
        }
      });

      if (ordersToComplete.length > 0) {
        // console.log(`Attempting to complete ${ordersToComplete.length} orders.`);
        const { error: updateError } = await supabase
          .from('orders')
          .update({
            status: 'completed',
            updated_at: new Date().toISOString()
          })
          .in('id', ordersToComplete);

        if (updateError) {
          console.error("Error updating orders to completed:", updateError); // IMPROVEMENT: Log error
        } else {
          // console.log(`${ordersToComplete.length} orders marked as completed.`);
          queryClient.invalidateQueries({ queryKey: ['userPurchases'] });
          queryClient.invalidateQueries({ queryKey: ['userSales'] });
          
          toast({
            title: "Porositë u përditësuan",
            description: `${ordersToComplete.length} porosi që ishin dorëzuar për më shumë se 30 ditë u shënuan si të përfunduara.`,
            duration: 5000,
          });
        }
      } else {
        // console.log("No orders met the criteria for auto-completion.");
      }
    } catch (error) { 
      console.error("Unexpected error in checkAndCompleteOrders:", error); // IMPROVEMENT: Log error
    } finally {
      isProcessingRef.current = false;
    }
  }, [queryClient, toast]); 

  useEffect(() => {
    checkAndCompleteOrders();

    const intervalId = setInterval(checkAndCompleteOrders, 24 * 60 * 60 * 1000); 

    return () => {
      clearInterval(intervalId);
      // console.log("Order auto-completion interval cleared.");
    };
  }, [checkAndCompleteOrders]); 
};

